import type { ReactNode } from "react";
import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  BACKNESS,
  type Backness,
  type ChartSymbol,
  codePointLabels,
  DOTTED_CIRCLE,
  HEIGHTS,
  type Height,
  isCombining,
  MANNERS,
  type Manner,
  PLACES,
  type Place,
  PULMONIC,
  VOWELS,
} from "@/lib/tool-kit/word-knowledge/ipa-chart";

/** A key the reader pressed: what to insert and what to call it. */
export interface PickedKey {
  text: string;
  name: string;
  combining: boolean;
}

/** The words the keys need, taken from the widget's strings. */
export interface KeyStrings {
  places: Record<Place, string>;
  manners: Record<Manner, string>;
  heights: Record<Height, string>;
  backness: Record<Backness, string>;
  voiceless: string;
  voiced: string;
  unrounded: string;
  rounded: string;
  /** `{voicing} {place} {manner}` */
  consonantName: string;
  /** `{height} {backness} {rounding} vowel` */
  vowelName: string;
  names: Record<string, string>;
  rowHeader: string;
  columnHeader: string;
}

const KEY =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-border bg-background px-2 text-foreground text-lg leading-none transition-colors hover:border-primary hover:bg-muted";

/** One symbol. The mouse-down default is blocked so the text box keeps its cursor. */
export function Key({
  text,
  name,
  onPick,
}: {
  text: string;
  name: string;
  onPick: (key: PickedKey) => void;
}) {
  const combining = isCombining(text);
  return (
    <button
      aria-label={name}
      className={KEY}
      lang="und-fonipa"
      onClick={() => onPick({ text, name, combining })}
      onMouseDown={(event) => event.preventDefault()}
      title={`${text} ${codePointLabels(text).join(" ")} ${name}`}
      type="button"
    >
      {combining ? DOTTED_CIRCLE + text : text}
    </button>
  );
}

/** A wrapped row of keys whose names come from the strings by id. */
export function KeyRow({
  symbols,
  strings,
  onPick,
}: {
  symbols: readonly ChartSymbol[];
  strings: KeyStrings;
  onPick: (key: PickedKey) => void;
}) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {symbols.map((symbol) => (
        <li key={symbol.id}>
          <Key
            name={strings.names[symbol.id] ?? symbol.id}
            onPick={onPick}
            text={symbol.text}
          />
        </li>
      ))}
    </ul>
  );
}

function Cell({ children }: { children: ReactNode }) {
  return <ul className="flex gap-1">{children}</ul>;
}

export function ConsonantTable({
  strings,
  onPick,
}: {
  strings: KeyStrings;
  onPick: (key: PickedKey) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              <span className="sr-only">{strings.rowHeader}</span>
            </th>
            {PLACES.map((place) => (
              <th className={`${TH} text-xs`} key={place} scope="col">
                {strings.places[place]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MANNERS.map((manner) => (
            <tr key={manner}>
              <th className={`${TD} whitespace-nowrap text-xs`} scope="row">
                {strings.manners[manner]}
              </th>
              {PLACES.map((place) => {
                const pair = PULMONIC[manner][place];
                return (
                  <td className={TD} key={place}>
                    {pair ? (
                      <Cell>
                        {pair.map((text, voiced) =>
                          text ? (
                            <li key={text}>
                              <Key
                                name={t(strings.consonantName, {
                                  voicing: voiced
                                    ? strings.voiced
                                    : strings.voiceless,
                                  place: strings.places[place],
                                  manner: strings.manners[manner],
                                })}
                                onPick={onPick}
                                text={text}
                              />
                            </li>
                          ) : null
                        )}
                      </Cell>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function VowelTable({
  strings,
  onPick,
}: {
  strings: KeyStrings;
  onPick: (key: PickedKey) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              <span className="sr-only">{strings.columnHeader}</span>
            </th>
            {BACKNESS.map((place) => (
              <th className={`${TH} text-xs`} key={place} scope="col">
                {strings.backness[place]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HEIGHTS.map((height) => (
            <tr key={height}>
              <th className={`${TD} whitespace-nowrap text-xs`} scope="row">
                {strings.heights[height]}
              </th>
              {BACKNESS.map((place) => {
                const pair = VOWELS[height][place];
                return (
                  <td className={TD} key={place}>
                    {pair ? (
                      <Cell>
                        {pair.map((text, rounded) =>
                          text ? (
                            <li key={text}>
                              <Key
                                name={t(strings.vowelName, {
                                  height: strings.heights[height],
                                  backness: strings.backness[place],
                                  rounding: rounded
                                    ? strings.rounded
                                    : strings.unrounded,
                                })}
                                onPick={onPick}
                                text={text}
                              />
                            </li>
                          ) : null
                        )}
                      </Cell>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
